import dayjs from "dayjs";
import { Booking } from "../types";
import { parseBookingNote } from "./bookings";

export type ContactSummary = {
  id: string;
  name: string;
  email: string;
  phone: string;
  bookings: Booking[];
  totalBookings: number;
  totalGuests: number;
  lastMeetingDate: string;
  nextMeetingDate: string;
  firstBookedAt: string;
  latestCreatedAt: string;
  eventTitles: string[];
};

export function buildContacts(bookings: Booking[]): ContactSummary[] {
  const contacts = new Map<string, ContactSummary>();
  const today = dayjs().format("YYYY-MM-DD");

  bookings.forEach((booking) => {
    const key = (booking.email || booking.phone || booking.name).trim().toLowerCase();
    if (!key) return;
    const current = contacts.get(key) || {
      id: key,
      name: booking.name,
      email: booking.email,
      phone: booking.phone,
      bookings: [],
      totalBookings: 0,
      totalGuests: 0,
      lastMeetingDate: "",
      nextMeetingDate: "",
      firstBookedAt: booking.createdAt,
      latestCreatedAt: booking.createdAt,
      eventTitles: [],
    };
    const meta = parseBookingNote(booking.note);
    const guests = parseGuestCount(meta.quantity);
    current.name = current.name || booking.name;
    current.email = current.email || booking.email;
    current.phone = current.phone || booking.phone;
    current.bookings.push(booking);
    current.totalBookings += 1;
    current.totalGuests += Number.isFinite(guests) ? guests : 0;
    current.firstBookedAt = minDateTime(current.firstBookedAt, booking.createdAt);
    current.latestCreatedAt = maxDateTime(current.latestCreatedAt, booking.createdAt);
    if (!current.eventTitles.includes(booking.eventTitle)) current.eventTitles.push(booking.eventTitle);
    if (booking.date < today) current.lastMeetingDate = maxDateTime(current.lastMeetingDate, booking.date);
    if (booking.date >= today) current.nextMeetingDate = minDateTime(current.nextMeetingDate, booking.date);
    contacts.set(key, current);
  });

  return Array.from(contacts.values())
    .map((contact) => ({
      ...contact,
      bookings: [...contact.bookings].sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`)),
    }))
    .sort((a, b) => {
      const aDate = a.nextMeetingDate || a.lastMeetingDate || a.latestCreatedAt;
      const bDate = b.nextMeetingDate || b.lastMeetingDate || b.latestCreatedAt;
      return bDate.localeCompare(aDate);
    });
}

function parseGuestCount(value: string) {
  const match = String(value || "").match(/\d+/);
  return match ? Number(match[0]) : 0;
}

export function minDateTime(current: string, next: string) {
  if (!current) return next;
  if (!next) return current;
  return next < current ? next : current;
}

export function maxDateTime(current: string, next: string) {
  if (!current) return next;
  if (!next) return current;
  return next > current ? next : current;
}

export function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
}

export function formatPhone(phone: string) {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("976")) return `+976 ${digits.slice(3, 7)} ${digits.slice(7)}`;
  if (digits.length === 8) return `+976 ${digits.slice(0, 4)} ${digits.slice(4)}`;
  return phone;
}
